/**
 * AdLibraryUpdate — bring new ads' details in from Meta's Ad Library, by copy and paste.
 *
 * Why it works this way: our server cannot read the Ad Library (Meta answers a request that
 * does not come from a browser with 403), and nobody here has Ads Manager access yet. An
 * admin's own browser CAN read it, so the admin opens the Library, copies the page and pastes
 * it here. The paste carries every ad's Library ID, run dates, wording and picture address
 * (src/utils/adLibraryPaste.js reads them out). The ad for a campaign is the one that started
 * on the day of the campaign's first click.
 *
 * Nothing is saved until "Save" is pressed, and only what an ad is missing is written: a name
 * someone gave, or a picture already stored, is never replaced.
 *
 * When Ads Manager access arrives this whole flow is replaced by reading the ads directly;
 * see docs/ANALYTICS-TOOL-STATUS.md → Ads.
 */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { Input, Select } from '../Form';
import InfoNotice from '../InfoNotice';
import { dayLabel } from '../../../utils/analyticsFormat';
import { AD_LIBRARY_SEARCH_URL } from '../../../utils/metaAds';
import { parseAdLibraryPaste, matchAdsToCampaigns, suggestAdName } from '../../../utils/adLibraryPaste';
import { tail } from '../../../utils/campaignDisplay';

const HOW = {
  'same-day': 'It started the day this ad was first clicked.',
  'day-before': 'It started the day before this ad’s first click.',
  several: 'More than one ad started that day. Choose the right one.',
  none: 'No ad in the Library started that day. If this is a second run of an earlier ad, choose that ad.',
  'unknown-start': 'We could not work out when this ad was first clicked. Choose its ad.',
};
const firstWords = (t) => { const s = String(t || '').split('\n').find((l) => l.trim()) || ''; return s.length > 70 ? `${s.slice(0, 68)}…` : s; };

export default function AdLibraryUpdate({ ads, starts, usedLibraryIds, onSaveAd, onClose, onDone }) {
  const [found, setFound] = useState(null); // ads read from the paste, or null before any paste
  const [choice, setChoice] = useState({}); // campaign → libraryId ('' = none)
  const [names, setNames] = useState({});
  const [status, setStatus] = useState({}); // campaign → { state: 'saving'|'saved'|'failed', notes: [] }
  const [saving, setSaving] = useState(false);
  const [matchedHow, setMatchedHow] = useState({}); // campaign → how its ad was matched

  const campaigns = useMemo(
    () => ads.map((a) => ({ key: a.campaign, firstDay: (starts[a.campaign] || {}).firstHitDay || null })),
    [ads, starts],
  );
  const byId = useMemo(() => Object.fromEntries((found || []).map((a) => [a.libraryId, a])), [found]);

  const onPaste = (e) => {
    e.preventDefault();
    const read = parseAdLibraryPaste({
      html: e.clipboardData.getData('text/html'),
      text: e.clipboardData.getData('text/plain'),
    });
    setFound(read);
    setStatus({});
    const matched = matchAdsToCampaigns(campaigns, read, usedLibraryIds);
    const nextChoice = {};
    const nextNames = {};
    ads.forEach((a) => {
      const m = matched[a.campaign] || {};
      nextChoice[a.campaign] = m.libraryId || '';
      const ad = read.find((x) => x.libraryId === m.libraryId);
      nextNames[a.campaign] = a.named ? a.label : (ad ? suggestAdName(ad.text, ad.startDay) : '');
    });
    setChoice(nextChoice);
    setNames(nextNames);
    setMatchedHow(Object.fromEntries(Object.entries(matched).map(([k, v]) => [k, v.how])));
  };
  const choose = (a, libraryId) => {
    setChoice((c) => ({ ...c, [a.campaign]: libraryId }));
    const ad = byId[libraryId];
    // A name someone already gave is kept; otherwise suggest one from the chosen ad.
    if (!a.named) setNames((n) => ({ ...n, [a.campaign]: ad ? suggestAdName(ad.text, ad.startDay) : '' }));
  };

  const ready = ads.filter((a) => choice[a.campaign] && (a.named || (names[a.campaign] || '').trim()) && (status[a.campaign] || {}).state !== 'saved');

  const save = async () => {
    setSaving(true);
    let savedAny = false;
    for (const a of ready) {
      const ad = byId[choice[a.campaign]];
      setStatus((s) => ({ ...s, [a.campaign]: { state: 'saving', notes: [] } }));
      // eslint-disable-next-line no-await-in-loop
      const result = await onSaveAd(a, { ad, name: (names[a.campaign] || '').trim() });
      if (result.ok) savedAny = true;
      setStatus((s) => ({ ...s, [a.campaign]: { state: result.ok ? 'saved' : 'failed', notes: result.notes } }));
    }
    setSaving(false);
    if (savedAny && onDone) onDone();
  };

  const allSaved = ads.length > 0 && ads.every((a) => (status[a.campaign] || {}).state === 'saved');

  return (
    <Modal isOpen onClose={onClose} title="Update ads from Meta’s Ad Library" size="large">
      <div className="ads-update">
        <p className="ads-update__intro">
          Use this when a new ad appears with no name or picture. It takes about a minute. Meta does not let
          our systems read its Ad Library, so you copy the page and paste it here.
        </p>

        <ol className="ads-update__steps">
          <li>
            <strong>Open Meta’s Ad Library.</strong>{' '}
            <a href={AD_LIBRARY_SEARCH_URL} target="_blank" rel="noopener noreferrer">Open the Ad Library ↗</a>
            <span>It opens in a new tab and lists every ClozeSure ad, running or finished. You do not need to log in.</span>
          </li>
          <li>
            <strong>Scroll to the bottom of that page</strong>
            <span>so every ad has loaded. Stop when no more ads appear.</span>
          </li>
          <li>
            <strong>Select the whole page and copy it.</strong>
            <span>Press <kbd>⌘</kbd> <kbd>A</kbd> then <kbd>⌘</kbd> <kbd>C</kbd> on a Mac (<kbd>Ctrl</kbd> <kbd>A</kbd> then <kbd>Ctrl</kbd> <kbd>C</kbd> on Windows).</span>
          </li>
          <li>
            <strong>Come back to this tab, click in the box below and paste.</strong>
            <span>Press <kbd>⌘</kbd> <kbd>V</kbd> (<kbd>Ctrl</kbd> <kbd>V</kbd> on Windows).</span>
          </li>
        </ol>

        <textarea
          className="ads-update__paste"
          aria-label="Paste the Ad Library page here"
          placeholder={found ? 'Paste again to replace what was read' : 'Click here, then paste'}
          value=""
          onChange={() => {}}
          onPaste={onPaste}
          rows={2}
        />

        {found && found.length === 0 && (
          <InfoNotice variant="warning">
            No ads were found in what you pasted. Make sure you copied the whole Ad Library page (step 3), not just
            part of it, then paste again.
          </InfoNotice>
        )}
        {found && found.length > 0 && (
          <p className="ads-update__found" role="status">
            Found {found.length} ad{found.length === 1 ? '' : 's'} in what you pasted.
            {found.some((a) => !a.imageUrl) && ' Some came without a picture; add those with “Paste picture” on the ad afterwards.'}
          </p>
        )}

        {ads.length === 0 ? (
          <InfoNotice variant="info">
            Every ad in this period already has its name, link, wording and picture. If a new ad is not listed,
            choose a longer period on the page behind this box.
          </InfoNotice>
        ) : (
          <div className="ads-update__list">
            <h4>{ads.length === 1 ? 'One ad needs details' : `${ads.length} ads need details`}</h4>
            {ads.map((a) => {
              const start = (starts[a.campaign] || {}).firstHitDay;
              const ad = byId[choice[a.campaign]];
              const st = status[a.campaign] || {};
              const wants = ['name', 'link', 'text', 'picture'].filter((k) => a.missing[k])
                .map((k) => ({ name: 'name', link: 'Ad Library link', text: 'wording', picture: 'picture' }[k]));
              return (
                <div key={a.campaign} className="ads-update__ad" role="group" aria-label={`Ad ${a.label}`}>
                  <div className="ads-update__pic">
                    {/* no-referrer: a Tools page address is not sent to Meta's image host. */}
                    {ad && ad.imageUrl
                      ? <img src={ad.imageUrl} alt="The ad chosen from the Library" referrerPolicy="no-referrer" />
                      : <span>no picture</span>}
                  </div>
                  <div className="ads-update__fields">
                    <div className="ads-update__which">
                      <strong>{a.named ? a.label : `Campaign ${tail(a.campaign)}`}</strong>
                      <span>
                        {start ? `first clicked ${dayLabel(start)}` : 'first click unknown'} · needs its {wants.join(', ')}
                      </span>
                    </div>
                    {!found ? (
                      <span className="ads-update__hint">Paste the Ad Library page above to find this ad.</span>
                    ) : (
                      <>
                        <label className="ads-update__label" htmlFor={`ad-${a.campaign}`}>Its ad in the Library</label>
                        <Select
                          id={`ad-${a.campaign}`}
                          value={choice[a.campaign] || ''}
                          onChange={(e) => choose(a, e.target.value)}
                          disabled={saving || st.state === 'saved'}
                          placeholder=""
                          fullWidth
                          options={[
                            { value: '', label: 'Not chosen' },
                            ...found.map((x) => ({
                              value: x.libraryId,
                              label: `${dayLabel(x.startDay)} · ${firstWords(x.text) || `Library ID ${x.libraryId}`}${usedLibraryIds.includes(x.libraryId) ? ' (already used by another ad)' : ''}`,
                            })),
                          ]}
                        />
                        <span className="ads-update__hint">
                          {HOW[matchedHow[a.campaign]] || ''}
                          {ad && ad.grouped > 1 && ' This Library entry stands for more than one run of the same ad.'}
                        </span>
                        {a.missing.name && (
                          <>
                            <label className="ads-update__label" htmlFor={`name-${a.campaign}`}>Name to show in Tools</label>
                            <Input
                              id={`name-${a.campaign}`}
                              value={names[a.campaign] || ''}
                              onChange={(e) => setNames((n) => ({ ...n, [a.campaign]: e.target.value }))}
                              placeholder="A short name for this ad"
                              disabled={saving || st.state === 'saved'}
                              maxLength={120}
                              fullWidth
                            />
                          </>
                        )}
                      </>
                    )}
                    {st.state && (
                      <span className={`ads-update__status ads-update__status--${st.state}`} role="status">
                        {st.state === 'saving' ? 'Saving…' : st.state === 'saved' ? 'Saved.' : 'Not everything was saved.'}
                        {(st.notes || []).length > 0 && ` ${st.notes.join(' ')}`}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="ads-update__actions">
          {ads.length > 0 && (
            <Button variant="primary" disabled={saving || ready.length === 0} onClick={save}>
              {saving ? 'Saving…' : ready.length === 1 ? 'Save 1 ad' : `Save ${ready.length} ads`}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} disabled={saving}>{allSaved ? 'Done' : 'Close'}</Button>
          {ads.length > 0 && found && ready.length === 0 && !allSaved && !saving && (
            <span className="ads-update__hint">Choose each ad’s Library entry{ads.some((a) => a.missing.name) ? ' and give it a name' : ''} to save it.</span>
          )}
        </div>
      </div>
    </Modal>
  );
}

AdLibraryUpdate.propTypes = {
  /** Ads lacking something (adsNeedingDetails), each with `missing`. */
  ads: PropTypes.array.isRequired,
  /** useAdStarts' map: `firstHitDay` per campaign is what an ad is matched on. */
  starts: PropTypes.object.isRequired,
  /** Library IDs other ads already carry, so one Library ad is not given to two campaigns. */
  usedLibraryIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** (ad row, { ad: pasted ad, name }) → Promise<{ ok: boolean, notes: string[] }> */
  onSaveAd: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Called once something was saved, so the page behind can reload its names. */
  onDone: PropTypes.func,
};
